import { useCallback } from 'react'

import { Radio } from '@circlefin/components'
import { useForm, useController } from '@circlefin/form'
import * as y from 'yup'

import { SIGNING_METHODS } from '../constants'

import type { SigningMethod } from '@features/cpn.hooks/useWalletValidator'

// Component-specific types
interface MethodSelectionForm {
  signingMethod: string
}

interface MethodSelectionProps {
  onMethodSelect: (method: SigningMethod) => void
}

interface SigningMethodRadioProps {
  signingMethods: Array<{
    value: string
    label: string
    description: string
    signingMethod: string
    chains: string[]
  }>
}

// Form validation schema
const methodSelectionSchema = y.object({
  signingMethod: y.string().required('Please select a signing method'),
})

/**
 * Radio group component for selecting signing methods.
 */
const SigningMethodRadio: React.FC<SigningMethodRadioProps> = ({
  signingMethods,
}) => {
  const { field } = useController<{ signingMethod: string }, 'signingMethod'>({
    name: 'signingMethod',
  })

  return (
    <Radio.Group
      className="space-y-3"
      data-testid="method-radio-group"
      direction="vertical"
      label="Signing Methods"
      name="signingMethod"
      onChange={field.onChange}
      value={field.value}
    >
      {signingMethods.map((method) => (
        <Radio
          key={method.value}
          aria-label={method.label}
          className="type-body-base"
          data-testid={`method-${method.value}`}
          value={method.value}
        >
          <div>
            <div className="text-neutral-strong type-body-base-bold">
              {method.label}
            </div>
            <div className="text-neutral-subtle type-body-sm">
              {method.description}
            </div>
          </div>
        </Radio>
      ))}
    </Radio.Group>
  )
}

/**
 * Method selection component for the first step of wallet compatibility testing
 * Allows users to choose between EIP-712, EIP-1559, and Solana signing methods.
 */
export const MethodSelection: React.FC<MethodSelectionProps> = ({
  onMethodSelect,
}) => {
  // Method Selection Form
  const [MethodForm] = useForm<MethodSelectionForm>({
    schema: methodSelectionSchema,
  })

  // Handle method selection submission
  const handleMethodSelection = useCallback(
    (data: MethodSelectionForm) => {
      const method = data.signingMethod as 'eip712' | 'eip1559' | 'solana'
      onMethodSelect(method)
    },
    [onMethodSelect],
  )

  return (
    <div className="space-y-3">
      <div>
        <h4 className="mb-2 text-neutral-strong type-h-title-md">
          Select Signing Method
        </h4>
        <p className="mb-0 text-neutral-subtle type-body-sm">
          Choose the type of signature you want to test with your wallet.
        </p>
      </div>

      <MethodForm
        className="space-y-4"
        data-testid="method-form"
        onSubmit={handleMethodSelection}
      >
        <SigningMethodRadio signingMethods={SIGNING_METHODS} />

        <MethodForm.SubmitButton
          className="w-full sm:w-auto"
          data-testid="continue-button"
          variant="primary"
        >
          Continue
        </MethodForm.SubmitButton>
      </MethodForm>
    </div>
  )
}
